import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getStaticRegistryItems, getRouteForItem } from './registry';
import { useRecordSearch } from './useRecordSearch';
import { CommandItem, CommandItemType } from './types';

type CommandBarSection = {
  title: string;
  items: CommandItem[];
};

type UseCommandBarResult = {
  query: string;
  setQuery: (q: string) => void;
  sections: CommandBarSection[];
  selectedIndex: number;
  totalItems: number;
  isLoading: boolean;
  handleSelect: (item: CommandItem) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
};

function matchesQuery(
  item: { label: string; keywords: string[] },
  terms: string[],
): boolean {
  if (terms.length === 0) return true;
  const haystack = [item.label, ...item.keywords].join(' ').toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

function scoreItem(
  item: { label: string; keywords: string[] },
  terms: string[],
): number {
  const labelLower = item.label.toLowerCase();
  let score = 0;
  for (const term of terms) {
    if (labelLower.startsWith(term)) score += 10;
    else if (labelLower.includes(term)) score += 5;
  }
  return score;
}

export function useCommandBar(
  onClose: () => void,
  onAiSelect: (query: string) => void,
): UseCommandBarResult {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const history = useHistory();
  const registryItems = useRef(getStaticRegistryItems());

  const terms = useMemo(
    () =>
      query
        .toLowerCase()
        .split(/\s+/)
        .filter((t) => t.length > 0),
    [query],
  );

  const { items: recordItems, isLoading } = useRecordSearch(query);

  const filteredStatic = useMemo(() => {
    if (terms.length === 0) return registryItems.current;
    return registryItems.current
      .filter((item) => matchesQuery(item, terms))
      .sort((a, b) => scoreItem(b, terms) - scoreItem(a, terms));
  }, [terms]);

  const sections = useMemo(() => {
    const result: CommandBarSection[] = [];

    if (terms.length > 0) {
      result.push({
        title: 'Employee Intelligence',
        items: [
          {
            id: 'ai-ask',
            type: 'ai' as CommandItemType,
            label: `Ask Assistant: ${query}`,
            icon: 'phosphor-star-four',
            keywords: [],
          },
        ],
      });
    }

    const actions = filteredStatic.filter((i) => i.type === 'action');
    if (actions.length > 0) {
      result.push({
        title: 'Actions',
        items: actions.slice(0, 5),
      });
    }

    const gotos = filteredStatic.filter((i) => i.type === 'goto');
    if (gotos.length > 0) {
      result.push({
        title: 'Go to',
        items: gotos.slice(0, 8),
      });
    }

    if (recordItems.length > 0) {
      result.push({
        title: 'People',
        items: recordItems,
      });
    }

    return result;
  }, [filteredStatic, recordItems, terms, query]);

  const totalItems = sections.reduce((acc, s) => acc + s.items.length, 0);

  useEffect(() => {
    if (totalItems === 1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(-1);
    }
  }, [query, totalItems]);

  const getItemAtIndex = useCallback(
    (index: number): CommandItem | null => {
      let cursor = 0;
      for (const section of sections) {
        if (index < cursor + section.items.length) {
          return section.items[index - cursor];
        }
        cursor += section.items.length;
      }
      return null;
    },
    [sections],
  );

  const handleSelect = useCallback(
    (item: CommandItem) => {
      if (item.type === 'ai') {
        onAiSelect(query);
        return;
      }

      if (item.type === 'record' && item.route) {
        history.push(item.route);
        onClose();
        return;
      }

      const registryItem = registryItems.current.find((r) => r.id === item.id);
      if (registryItem) {
        history.push(getRouteForItem(registryItem));
        onClose();
      }
    },
    [history, onClose, onAiSelect, query],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
          break;
        case 'Enter': {
          if (selectedIndex < 0) break;
          e.preventDefault();
          const item = getItemAtIndex(selectedIndex);
          if (item) handleSelect(item);
          break;
        }
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [totalItems, selectedIndex, getItemAtIndex, handleSelect, onClose],
  );

  return {
    query,
    setQuery,
    sections,
    selectedIndex,
    totalItems,
    isLoading,
    handleSelect,
    handleKeyDown,
  };
}
